import { PropsWithChildren, ReactNode, useCallback, useEffect, useRef } from 'react';
import styles from './ResizableDraggableModal.module.css';
import { createPortal } from 'react-dom';
import { Fullscreen, Minimize, Close } from '@mui/icons-material';
import debounce from '../../lib/helper/debounce';

export type OnResizeType = {
    resizedData: ResizeObserverEntry['contentRect'];
};

export type ResizableDraggableModalProps = {
    open: boolean;
    onClose?: (e: MouseEvent, reason: 'escapeKeyDown' | 'closeIconClick') => void;
    onOpen?: () => void;
    onResize?: (data: DOMRectReadOnly) => void;
    title: ReactNode | undefined;
    size: { width: number; height: number };
} & PropsWithChildren;

const ResizableDraggableModal = ({
    open,
    onClose,
    onOpen,
    onResize,
    title,
    children,
    size
}: ResizableDraggableModalProps) => {
    const refBox = useRef<HTMLDivElement>(null);
    const refBreak = useRef<HTMLHRElement>(null);
    const refTitle = useRef<HTMLDivElement>(null);
    const refContent = useRef<HTMLDivElement>(null);
    const refCloseIcon = useRef<HTMLDivElement>(null);
    const refMinimizeIcon = useRef<HTMLDivElement>(null);
    const refFullScreenIcon = useRef<HTMLDivElement>(null);

    const refObserver = useRef(
        new ResizeObserver(
            debounce((mutations) => {
                onResize && onResize(mutations[0].contentRect);
            }, 200)
        )
    );

    useEffect(() => {
        open && onOpen && onOpen();
    }, [onOpen, open]);

    // resize
    useEffect(() => {
        const boxElement = refBox.current;
        if (!boxElement) return;

        refObserver.current.observe(boxElement);

        boxElement.style.width = `${size.width}px`;
        boxElement.style.height = `${size.height}px`;
    });

    useEffect(() => {
        const boxElement = refBox.current;
        if (!boxElement) return;

        const breakElement = refBreak.current;
        if (!breakElement) return;

        const fullScreenElement = refFullScreenIcon.current;
        if (!fullScreenElement) return;

        const titleElement = refTitle.current;
        if (!titleElement) return;

        const contentElement = refContent.current;
        if (!contentElement) return;

        const minimizeElement = refMinimizeIcon.current;
        if (!minimizeElement) return;

        const closeElement = refCloseIcon.current;
        if (!closeElement) return;

        const boxElementOldStyles = { ...boxElement.style };
        const titleElementOldStyles = { ...titleElement.style };
        const breakElementOldStyles = { ...breakElement.style };
        const contentElementOldStyles = { ...contentElement.style };

        const minimize = () => {
            contentElement.style.display = 'none';

            boxElement.style.maxHeight = '0';
            boxElement.style.minHeight = '70px';
            boxElement.style.overflow = 'hidden';
            boxElement.style.height = titleElement.style.height;
            boxElement.style.padding = '15px';
            boxElement.style.width = 'auto';
            boxElement.style.resize = 'none';
            boxElement.style.borderRadius = '5px';

            titleElement.style.width = 'auto';

            breakElement.style.display = 'none';
        };

        const unminimize = () => {
            contentElement.style.display = contentElementOldStyles.display;

            boxElement.style.maxHeight = boxElementOldStyles.maxHeight;
            boxElement.style.minHeight = boxElementOldStyles.minHeight;
            boxElement.style.overflow = boxElementOldStyles.overflow;
            boxElement.style.height = boxElementOldStyles.height;
            boxElement.style.width = boxElementOldStyles.width;
            boxElement.style.resize = boxElementOldStyles.resize;

            titleElement.style.width = titleElementOldStyles.width;

            breakElement.style.display = breakElementOldStyles.display;
        };

        const minimizeClickHandler = (event: MouseEvent) => {
            if (contentElement.style.display !== 'none') {
                minimize();
            } else {
                unminimize();
            }
        };

        const fullScreenClickHandler = (event: MouseEvent) => {
            if (boxElement.style.width !== '100%') {
                if (contentElement.style.display === 'none') {
                    unminimize();
                }

                boxElement.style.width = '100%';
                boxElement.style.height = '100%';
                boxElement.style.top = `${window.scrollY}px`;
                boxElement.style.left = '0';
                boxElement.style.borderRadius = '0';
                boxElement.style.resize = 'none';

                const html = document.querySelector('html') as HTMLHtmlElement;
                if (html.style.overflow === '') {
                    html.style.overflow = 'hidden';
                }
            } else {
                boxElement.style.width = boxElementOldStyles.width;
                boxElement.style.height = boxElementOldStyles.height;
                boxElement.style.top = boxElementOldStyles.top;
                boxElement.style.left = boxElementOldStyles.left;
                boxElement.style.borderRadius = boxElementOldStyles.borderRadius;
                boxElement.style.resize = boxElementOldStyles.resize;

                const html = document.querySelector('html') as HTMLHtmlElement;
                if (html.style.overflow === 'hidden') {
                    html.style.removeProperty('overflow');
                }
            }
        };

        const closeIconClickHandler = (event: MouseEvent) => {
            onClose && onClose(event, 'closeIconClick');
        };

        minimizeElement.addEventListener('click', minimizeClickHandler);
        fullScreenElement.addEventListener('click', fullScreenClickHandler);

        closeElement.addEventListener('click', closeIconClickHandler);

        return () => {
            minimizeElement.removeEventListener('click', minimizeClickHandler);
            fullScreenElement.removeEventListener('click', fullScreenClickHandler);
            closeElement.removeEventListener('click', closeIconClickHandler);
        };
    });

    useEffect(() => {
        const boxElement = refBox.current;
        const draggableHeader = refTitle.current;

        if (!boxElement) return;
        if (!draggableHeader) return;

        let pos1 = 0,
            pos2 = 0,
            pos3 = 0,
            pos4 = 0;

        const dragMouseDown = (event: MouseEvent) => {
            event.preventDefault();
            // get the mouse cursor position at startup:
            pos3 = event.clientX;
            pos4 = event.clientY;
            document.addEventListener('mouseup', closeDragElement);
            // call a function whenever the cursor moves:
            document.addEventListener('mousemove', elementDrag);
        };

        const elementDrag = (event: MouseEvent) => {
            event.preventDefault();
            // calculate the new cursor position:
            pos1 = pos3 - event.clientX;
            pos2 = pos4 - event.clientY;
            pos3 = event.clientX;
            pos4 = event.clientY;
            // set the element's new position:
            boxElement.style.top = boxElement.offsetTop - pos2 + 'px';
            boxElement.style.left = boxElement.offsetLeft - pos1 + 'px';
        };

        const closeDragElement = () => {
            document.removeEventListener('mousemove', elementDrag);
        };

        draggableHeader.addEventListener('mousedown', dragMouseDown);

        return () => {
            draggableHeader.removeEventListener('mousedown', dragMouseDown);
        };
    });

    useEffect(() => {
        const existingModals = Array.from(document.getElementsByClassName('mdnav-modal'));
        if (existingModals.length >= 1) {
            for (const container of existingModals) {
                if (container.childElementCount <= 0) {
                    container.remove();
                }
            }
        }
    });

    const createContainer = useCallback(() => {
        const modalContainer = document.createElement('div');
        modalContainer.classList.add('mdnav-modal');
        document.body.appendChild(modalContainer);
        return modalContainer;
    }, []);

    return createPortal(
        <>
            {open && (
                <div ref={refBox} className={styles.box}>
                    <div className={styles.header}>
                        <div ref={refTitle} className={styles.title}>
                            {title}
                        </div>
                        <div className={styles.icons}>
                            <div ref={refMinimizeIcon} className={styles.icon}>
                                <Minimize />
                            </div>
                            <div ref={refFullScreenIcon} className={styles.icon}>
                                <Fullscreen />
                            </div>
                            <div ref={refCloseIcon} className={styles.icon}>
                                <Close />
                            </div>
                        </div>
                    </div>
                    <hr ref={refBreak} />
                    <div ref={refContent} className={styles.content}>
                        {children}
                    </div>
                </div>
            )}
        </>,
        createContainer()
    );
};

export default ResizableDraggableModal;
